import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { base_url } from "../../utils/axiosConfig";
import { Modal, Button, Form, Spinner, Dropdown } from "react-bootstrap";
import { IoSearch, IoClose } from "react-icons/io5";
import { BsEye } from "react-icons/bs";
import { TbRefresh } from "react-icons/tb";
import { HiOutlineDotsVertical } from "react-icons/hi";
import Select from "react-select";
import toast from "react-hot-toast";
import { motion, AnimatePresence } from "framer-motion";
import './DeliveryBoy.css';

/* ─── Senior Modal Portal ──────────────────── */
const OrderModalPortal = ({ show, onHide, title, children, footer }) => {
  useEffect(() => {
    if (show) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [show]);

  if (!show) return null;

  const content = (
    <div className="db-portal-overlay" onClick={onHide}>
      <div className="db-portal-content" onClick={(e) => e.stopPropagation()}>
        <div className="db-portal-header">
          <h5 style={{ fontWeight: 800, margin: 0, fontSize: 18 }}>{title}</h5>
          <button className="db-portal-close" onClick={onHide}>
            <IoClose size={20} />
          </button>
        </div>
        <div className="db-portal-body">
          {children}
        </div>
        {footer ? (
          <div className="db-modal-footer-responsive">
            {footer}
          </div>
        ) : (
          <div className="p-3 border-top text-center">
              <button className="db-btn-ghost w-100 fw-bold rounded-3" onClick={onHide}>
                  Close Details
              </button>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(content, document.body);
};


/* ─── Helpers ──────────────────────────────── */
const getBadgeClass = (status) => {
  if (status === "Delivered")        return "db-status-badge badge-delivered";
  if (status === "Return")           return "db-status-badge badge-return";
  if (status === "Out for Delivery") return "db-status-badge badge-ofd";
  if (status === "Pending")          return "db-status-badge badge-pending";
  return "db-status-badge badge-default";
};

const selectStyles = {
  control: (b) => ({
    ...b,
    borderRadius: 10,
    padding: '2px 4px',
    borderColor: 'var(--db-border)',
    fontSize: 13,
    boxShadow: 'none',
    '&:hover': { borderColor: 'var(--db-primary)' },
  }),
  option: (b, { isSelected }) => ({
    ...b,
    fontSize: 13,
    background: isSelected ? 'var(--db-primary)' : b.background,
  }),
};

const STATUS_OPTIONS = [
  { value: "Out for Delivery", label: "Out for Delivery" },
  { value: "Delivered",        label: "Delivered" },
  { value: "Return",           label: "Return" },
];
const RETURN_OPTIONS = [
  { value: "Damaged Product", label: "Damaged Product" },
  { value: "Wrong Item",      label: "Wrong Item / Wrong Address" },
  { value: "Refused Order",   label: "Refused Order" },
  { value: "Others",          label: "Others" },
];
const FILTER_TABS = ["", "Out for Delivery", "Delivered", "Return"];
const UPDATABLE   = ["Out for Delivery", "Ready to Ship"];
const PAGE_SIZE   = 10;

/* ─── Component ────────────────────────────── */
const AssignOrders = () => {
  const [orders,        setOrders]       = useState([]);
  const [search,        setSearch]       = useState("");
  const [page,          setPage]         = useState(1);
  const [statusFilter,  setStatusFilter] = useState("");
  const [loading,       setLoading]      = useState(false);
  const [uploading,     setUploading]    = useState(false);
  const [showItems,     setShowItems]    = useState(false);

  /* update modal */
  const [showModal,     setShowModal]    = useState(false);
  const [selectedOrder, setSelected]     = useState(null);
  const [newStatus,     setNewStatus]    = useState(null);
  const [file,          setFile]         = useState(null);
  const [returnReason,  setReturnReason] = useState(null);
  const [customReason,  setCustomReason] = useState("");

  /* preview modal (Portal) */
  const [showPreview,   setShowPreview]  = useState(false);
  const [previewOrder,  setPreview]      = useState(null);

  /* ── fetch ── */
  const fetchOrders = async () => {
    try {
      setLoading(true);
      const raw = localStorage.getItem("customer");
      if (!raw) return toast.error("Not authenticated");
      const { _id: userId } = JSON.parse(raw);
      if (!userId) return toast.error("Invalid session");
      const res = await axios.get(`${base_url}orders/delvery-boy/orders/${userId}`);
      setOrders(res.data.orders || []);
    } catch {
      toast.error("Failed to load orders ❌");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchOrders(); }, []);

  /* ── update status ── */
  const handleSave = async () => {
    if (!selectedOrder || !newStatus) return toast.error("Select a status first");
    try {
      setUploading(true);
      const fd = new FormData();
      fd.append("orderId", selectedOrder._id);
      fd.append("status", newStatus.value);
      if (newStatus.value === "Return") {
        fd.append("returnReason", returnReason?.value === "Others" ? customReason : (returnReason?.value || "Not specified"));
      }
      if (file) fd.append("orderImage", file);
      await axios.put(`${base_url}orders/orders/update-status`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Status updated to "${newStatus.value}" 🎉`);
      await fetchOrders();
      setShowModal(false);
    } catch (err) {
      toast.error(err.response?.data?.message || "Update failed ❌");
    } finally {
      setUploading(false);
    }
  };

  const openModal = (order) => {
    setSelected(order);
    setNewStatus(null);
    setFile(null);
    setReturnReason(null);
    setCustomReason("");
    setShowModal(true);
  };

  /* ── filter / paginate ── */
  const filtered = orders.filter((o) => {
    const q = search.toLowerCase();
    const textMatch =
      o.trackingNumber?.toLowerCase().includes(q) ||
      o.shippingInfo?.firstName?.toLowerCase().includes(q) ||
      o.shippingInfo?.lastName?.toLowerCase().includes(q) ||
      o.shippingInfo?.city?.toLowerCase().includes(q) ||
      o.shippingInfo?.pincode?.toString().includes(q) ||
      o.orderStatus?.toLowerCase().includes(q);
    const statusMatch = !statusFilter || o.orderStatus === statusFilter;
    return textMatch && statusMatch;
  });

  const totalPages    = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated     = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  /* ─────────────────────────────────────────── */
  return (
    <div>
      {/* Page header */}
      <div className="db-page-header d-flex align-items-center justify-content-between">
        <div>
          <h1 className="db-page-title">Assigned Orders</h1>
          <p className="db-page-subtitle">{orders.length} order{orders.length !== 1 ? "s" : ""} assigned to you</p>
        </div>
        <button className="db-btn-primary" onClick={fetchOrders}>
          <TbRefresh size={16} /> Refresh
        </button>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="db-card"
      >
        {/* Action bar */}
        <div className="db-action-bar">
          <div className="db-search-wrap">
            <IoSearch className="db-search-icon" />
            <input
              type="text"
              placeholder="Search orders..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="db-search-input form-control"
            />
          </div>

          <div className="db-filter-pills">
            {FILTER_TABS.map((tab) => (
              <button
                key={tab}
                className={`db-filter-pill ${statusFilter === tab ? "active" : ""}`}
                onClick={() => { setStatusFilter(tab); setPage(1); }}
              >
                {tab || "All"}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '60px 0', gap: 16 }}>
            <div className="db-spinner" />
            <p style={{ color: 'var(--db-text-secondary)', fontWeight: 600, margin: 0 }}>Loading orders…</p>
          </div>
        ) : paginated.length === 0 ? (
          <div className="db-empty-state">
            <div style={{ fontSize: 48, marginBottom: 12 }}>📦</div>
            <h3 className="db-empty-state-title">No orders found</h3>
            <p className="db-empty-state-sub">Try adjusting your search or filter.</p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="db-table">
              <thead>
                <tr>
                  <th>S.No</th>
                  <th>Order</th>
                  <th>Customer</th>
                  <th>Address</th>
                  <th>Payment</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th style={{ textAlign: 'center' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                <AnimatePresence mode="popLayout">
                  {paginated.map((order, index) => (
                    <motion.tr
                      key={order._id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      layout
                    >
                      {/* ✅ S.No */}
                      <td>
                        {(page - 1) * PAGE_SIZE + index + 1}
                      </td> 
                      <td>
                        <div style={{ fontWeight: 700, fontSize: 13 }}>
                          #{order.trackingNumber?.slice(-8).toUpperCase() || "N/A"}
                        </div>
                        <div style={{ color: 'var(--db-text-secondary)', fontSize: 11, marginTop: 1 }}>
                          …{order._id.slice(-6)}
                        </div>
                      </td>
                      <td>
                        <div style={{ fontWeight: 600 }}>
                          {order.shippingInfo?.firstName} {order.shippingInfo?.lastName}
                        </div>
                        <div style={{ color: 'var(--db-text-secondary)', fontSize: 12, marginTop: 1 }}>
                          {order.user?.phoneNumber || "—"}
                        </div>
                      </td>
                      <td>
                        <div style={{ maxWidth: 180, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {order.shippingInfo?.city}, {order.shippingInfo?.pincode}
                        </div>
                        <div style={{ maxWidth: 180, fontSize: 11, color: 'var(--db-text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {order.shippingInfo?.address}
                        </div>
                      </td>
                      <td>
                        <span className={order.paymentInfo?.cashOndelivery ? "db-payment-tag tag-cod" : "db-payment-tag tag-prepaid"}>
                          {order.paymentInfo?.cashOndelivery ? "COD" : "PREPAID"}
                        </span>
                      </td>
                      <td style={{ fontWeight: 700, color: 'var(--db-primary)' }}>
                        ₹{order.totalPrice}
                      </td>
                      <td>
                        <span className={getBadgeClass(order.orderStatus)}>
                          {order.orderStatus}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                          <Dropdown align="end">
                            <Dropdown.Toggle variant="link" className="db-dropdown-toggle">
                              <HiOutlineDotsVertical size={18} />
                            </Dropdown.Toggle>
                            <Dropdown.Menu className="db-dropdown-menu">
                              <Dropdown.Item className="db-dropdown-item" onClick={() => { setPreview(order); setShowPreview(true); }}>
                                <BsEye /> View Details
                              </Dropdown.Item>
                              {UPDATABLE.includes(order.orderStatus) && (
                                <Dropdown.Item className="db-dropdown-item" onClick={() => openModal(order)}>
                                  <TbRefresh /> Update Status
                                </Dropdown.Item>
                              )}
                            </Dropdown.Menu>
                          </Dropdown>
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </AnimatePresence>
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="db-pagination">
            <span style={{ fontSize: 13, color: 'var(--db-text-secondary)' }} className="d-none d-sm-inline">
              Showing <b>{(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)}</b> of <b>{filtered.length}</b>
            </span>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <button 
                className="db-page-btn" 
                disabled={page === 1} 
                onClick={() => { setPage((p) => p - 1); window.scrollTo(0,0); }}
                title="Previous Page"
              >
                <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path d="M15 19l-7-7 7-7" /></svg>
              </button>

              <div className="d-flex gap-1">
                {[...Array(totalPages)].map((_, i) => {
                  const pg = i + 1;
                  // Show current page, first, last, and pages around current
                  if (
                    pg === 1 || 
                    pg === totalPages || 
                    (pg >= page - 1 && pg <= page + 1)
                  ) {
                    return (
                      <button
                        key={pg}
                        className={`db-page-btn ${page === pg ? "active" : ""}`}
                        onClick={() => { setPage(pg); window.scrollTo(0,0); }}
                        style={page === pg ? {
                          background: 'var(--db-primary)',
                          color: 'white',
                          borderColor: 'var(--db-primary)',
                          fontWeight: '700'
                        } : {}}
                      >
                        {pg}
                      </button>
                    );
                  } else if (pg === page - 2 || pg === page + 2) {
                    return <span key={pg} style={{ color: 'var(--db-text-secondary)', padding: '0 4px' }}>…</span>;
                  }
                  return null;
                })}
              </div>

              <button 
                className="db-page-btn" 
                disabled={page === totalPages} 
                onClick={() => { setPage((p) => p + 1); window.scrollTo(0,0); }}
                title="Next Page"
              >
                <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path d="M9 5l7 7-7 7" /></svg>
              </button>
            </div>
          </div>
        )}
      </motion.div>

      {/* ── Update Status Modal (Portal) ── */}
      <OrderModalPortal
        show={showModal}
        onHide={() => setShowModal(false)}
        title="Update Order Status"
        footer={(
          <>
            <Button variant="light" className="flex-grow-1 fw-bold rounded-3 py-2" onClick={() => setShowModal(false)} disabled={uploading}>
              Cancel
            </Button>
            <Button
              className="flex-grow-1 fw-bold rounded-3 py-2"
              style={{ background: 'var(--db-primary)', border: 'none' }}
              onClick={handleSave}
              disabled={
                !newStatus ||
                (newStatus.value === "Delivered" && !file) ||
                (newStatus.value === "Return" && !returnReason) ||
                (newStatus.value === "Return" && returnReason?.value === "Others" && !customReason) ||
                uploading
              }
            >
              {uploading ? <><Spinner as="span" animation="border" size="sm" className="me-2" />Saving…</> : "Save Status"}
            </Button>
          </>
        )}
      >
        <Form>
          <Form.Group className="mb-4">
            <label className="db-form-label">Select New Status</label>
            <Select
              options={STATUS_OPTIONS}
              value={newStatus}
              onChange={setNewStatus}
              placeholder="Choose status…"
              styles={selectStyles}
            />
          </Form.Group>

          <AnimatePresence mode="wait">
            {newStatus?.value === "Delivered" && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
              >
                <Form.Group className="mb-4">
                  <label className="db-form-label">Upload Delivery Proof</label>
                  <label htmlFor="proofUpload" className={`db-upload-box ${file ? "has-file" : ""}`}>
                    <input type="file" accept="image/*" id="proofUpload" className="d-none" onChange={(e) => setFile(e.target.files[0])} />
                    <div style={{ fontSize: 32, marginBottom: 8 }}>📷</div>
                    <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--db-primary)' }}>
                      {file ? "Change Image" : "Click to select image"}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--db-text-secondary)', marginTop: 4 }}>
                      {file ? file.name : "PNG or JPG, max 5MB"}
                    </div>
                  </label>
                </Form.Group>
              </motion.div>
            )}

            {newStatus?.value === "Return" && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
              >
                <Form.Group className="mb-4">
                  <label className="db-form-label">Return Reason</label>
                  <Select
                    options={RETURN_OPTIONS}
                    value={returnReason}
                    onChange={setReturnReason}
                    placeholder="Select a reason…"
                    styles={selectStyles}
                  />
                  <AnimatePresence>
                    {returnReason?.value === "Others" && (
                      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          placeholder="Describe the reason…"
                          value={customReason}
                          onChange={(e) => setCustomReason(e.target.value)}
                          style={{ marginTop: 12, borderRadius: 10, fontSize: 13, border: '1.5px solid var(--db-border)', resize: 'none' }}
                        />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </Form.Group>
              </motion.div>
            )}
          </AnimatePresence>
        </Form>
      </OrderModalPortal>

      {/* ── Preview Modal Portal (SENIOR GRADE) ── */}
      <OrderModalPortal 
        show={showPreview} 
        onHide={() => { setShowPreview(false); setShowItems(false); }}
        title="Order Details"
      >
          {previewOrder && (
            <div className="row g-3">
              {/* Tracking + Payment */}
              <div className="col-sm-6">
                <div style={{ background: 'var(--db-bg)', borderRadius: 12, padding: 16 }}>
                  <p className="db-form-label">Tracking</p>
                  <p style={{ fontWeight: 800, margin: '0 0 4px', wordBreak: 'break-all' }}>
                    #{previewOrder.trackingNumber?.toUpperCase()}
                  </p>
                  <p style={{ fontSize: 12, color: 'var(--db-text-secondary)', margin: '0 0 10px' }}>
                    {previewOrder.createdAt ? new Date(previewOrder.createdAt).toLocaleDateString() : "—"}
                  </p>
                  <span className={getBadgeClass(previewOrder.orderStatus)}>{previewOrder.orderStatus}</span>
                </div>
              </div>
              <div className="col-sm-6">
                <div style={{ background: 'var(--db-bg)', borderRadius: 12, padding: 16 }}>
                  <p className="db-form-label">Payment</p>
                  <p style={{ fontWeight: 800, fontSize: 22, margin: '0 0 4px' }}>₹{previewOrder.totalPrice}</p>
                  <span className={previewOrder.paymentInfo?.cashOndelivery ? "db-payment-tag tag-cod" : "db-payment-tag tag-prepaid"}>
                    {previewOrder.paymentInfo?.cashOndelivery ? "CASH ON DELIVERY" : "PREPAID"}
                  </span>
                </div>
              </div>

              {/* Customer + Address */}
              <div className="col-12">
                <div style={{ border: '1px solid var(--db-border)', borderRadius: 12, padding: 16 }}>
                  <div className="row g-3">
                    <div className="col-sm-6">
                      <p className="db-form-label">Customer</p>
                      <p style={{ fontWeight: 700, margin: '0 0 2px' }}>
                        {previewOrder.shippingInfo?.firstName} {previewOrder.shippingInfo?.lastName}
                      </p>
                      <p style={{ fontSize: 12, color: 'var(--db-text-secondary)', margin: '0 0 2px' }}>{previewOrder.user?.email}</p>
                      <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--db-primary)', margin: 0 }}>
                        📞 {previewOrder.user?.phoneNumber || "—"}
                      </p>
                    </div>
                    <div className="col-sm-6">
                      <p className="db-form-label">Shipping Address</p>
                      <p style={{ fontWeight: 600, margin: '0 0 2px', fontSize: 13 }}>{previewOrder.shippingInfo?.address}</p>
                      <p style={{ fontSize: 12, color: 'var(--db-text-secondary)', margin: 0 }}>
                        {previewOrder.shippingInfo?.city}, {previewOrder.shippingInfo?.state} – {previewOrder.shippingInfo?.pincode}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Return/Cancellation reason */}
              {(previewOrder.orderStatus?.toLowerCase() === "return" || previewOrder.orderStatus?.toLowerCase() === "cancelled") && (
                <div className="col-12">
                  <div style={{ background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.15)', borderRadius: 10, padding: 14 }}>
                    <p className="db-form-label" style={{ color: 'var(--db-danger)' }}>Reason</p>
                    <p style={{ margin: 0, fontWeight: 600, fontSize: 13 }}>
                      {previewOrder.cancellationReason || previewOrder.returnReason || "No reason provided"}
                    </p>
                  </div>
                </div>
              )}

              {/* Items */}
              <div className="col-12">
                <div style={{ border: '1px solid var(--db-border)', borderRadius: 12, overflow: 'hidden' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid var(--db-border)' }}>
                    <p className="db-form-label" style={{ margin: 0 }}>Order Items ({previewOrder.orderItems?.length || 0})</p>
                    <button className="db-btn-ghost" style={{ padding: '4px 12px', fontSize: 12 }} onClick={() => setShowItems(!showItems)}>
                      {showItems ? "Hide" : "Show"}
                    </button>
                  </div>
                  {showItems && (
                    <div>
                      {previewOrder.orderItems?.map((item, idx) => (
                        <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: idx < previewOrder.orderItems.length - 1 ? '1px solid var(--db-border)' : 'none' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <div style={{ background: 'var(--db-bg)', borderRadius: 8, padding: '6px 10px', fontWeight: 700, fontSize: 13, minWidth: 40, textAlign: 'center' }}>
                              {item.quantity}x
                            </div>
                            <div>
                              <p style={{ margin: 0, fontWeight: 600, fontSize: 13 }}>{item.product?.title || "Product"}</p>
                              <p style={{ margin: 0, fontSize: 11, color: 'var(--db-text-secondary)' }}>₹{item.price} each</p>
                            </div>
                          </div>
                          <span style={{ fontWeight: 800, fontSize: 14 }}>₹{item.price * item.quantity}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Proof image */}
              {previewOrder.orderImage && (
                <div className="col-12 text-center">
                  <p className="db-form-label">Delivery Proof</p>
                  <img
                    src={`${base_url}${previewOrder.orderImage}`}
                    alt="Delivery proof"
                    style={{ maxHeight: 300, width: '100%', objectFit: 'contain', borderRadius: 12 }}
                  />
                </div>
              )}
            </div>
          )}
      </OrderModalPortal>
    </div>
  );
};

export default AssignOrders;